import { MAX_CALENDAR_DATE, MIN_CALENDAR_DATE } from 'src/app/shared/misc/dates';
import { TodayDate } from 'src/app/shared/misc/today.date';
import { ValidationStatus } from './validationStatus';
import { TranslatableString } from '../i18n/TranslatableString';
import { TranslatableStringType } from '../i18n/types';

export interface ValidationProps {
  min?: number;
  max?: number;
  minDate?: Date;
  maxDate?: Date;
  label?: string;
}

export interface ValidationStrings {
  title: { key: string; label?: string };
  text: { key: string; params?: Record<string, string> };
}

// eslint-disable-next-line max-lines-per-function, complexity
export function getValidationStrings(
  status: ValidationStatus,
  {
    label,
    min = Number.MIN_SAFE_INTEGER,
    max = Number.MAX_SAFE_INTEGER,
    minDate = MIN_CALENDAR_DATE,
    maxDate = MAX_CALENDAR_DATE,
  }: ValidationProps = {},
): ValidationStrings {
  if (status === ValidationStatus.MissingValue) {
    return {
      title: { key: `${status}Title`, label },
      text: null,
    };
  }

  if (status === ValidationStatus.InvalidDateFormat) {
    return {
      title: { key: `${status}Title`, label },
      text: { key: `${status}Text` },
    };
  }

  if (status === ValidationStatus.InvalidNumber) {
    return {
      title: { key: `${status}Title`, label },
      text: { key: `${status}Text` },
    };
  }

  if (status === ValidationStatus.InvalidDate) {
    return {
      title: { key: `${status}Title`, label },
      text: { key: `${status}Text` },
    };
  }

  if (status === ValidationStatus.LessThanMin) {
    return {
      title: { key: `${status}Title`, label },
      text: { key: `${status}Text`, params: { min: `${min}` } },
    };
  }

  if (status === ValidationStatus.GreaterThanMax) {
    return {
      title: { key: `${status}Title`, label },
      text: { key: `${status}Text`, params: { max: `${max}` } },
    };
  }

  if (status === ValidationStatus.NegativeValue) {
    return {
      title: { key: `${status}Title`, label },
      text: { key: `${status}Text` },
    };
  }

  if (status === ValidationStatus.BeforeMinDate) {
    return {
      title: { key: `${status}Title`, label },
      text: { key: `${status}Text`, params: { minDate: `${TodayDate.getIsoFormattedDate(minDate)}` } },
    };
  }

  if (status === ValidationStatus.AfterMaxDate) {
    return {
      title: { key: `${status}Title`, label },
      text: { key: `${status}Text`, params: { maxDate: `${TodayDate.getIsoFormattedDate(maxDate)}` } },
    };
  }

  return { title: { key: `${ValidationStatus.Ok}Title` }, text: null };
}

const getTitleWithLabel = (title: string, label?: string): string => (label ? `${title} [${label}]` : title);

const isStringType = (value: unknown): value is string =>
  typeof value === 'string' || value instanceof TranslatableString;

export const toTitleMessage = (
  i18n: Record<string, TranslatableStringType> | TranslatableStringType,
  value: ValidationStrings['title'],
): string => {
  if (!value?.key) {
    return '';
  }
  if (isStringType(i18n)) {
    return getTitleWithLabel(i18n, value.label);
  }
  return getTitleWithLabel(i18n[value.key] || value.key, value.label);
};

// eslint-disable-next-line complexity
export const toTextMessage = (
  i18n: Record<string, TranslatableStringType> | TranslatableStringType,
  value: ValidationStrings['text'],
): string => {
  if (!value?.key) {
    return '';
  }
  if (isStringType(i18n)) {
    return i18n?.withParams(value.params || {}) || value.key;
  }
  return i18n[value.key]?.withParams(value.params || {}) || value.key;
};
